import {
  DefaultRootPricing,
  MinimalPricing,
  PricingForms,
  PricingRate,
  PricingRateFields,
  PricingSupportingCountries,
} from '@/services/Pricing/pricing.service';
import { UseFormWatch } from 'react-hook-form';
import { CurrencyCode, formatMinorUnitsToString, getMinorUnits } from '@final-commerce/common';

/**
 * Normalize decimal separator (comma to period) for parsing
 */
export const normalizeDecimalSeparator = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value).replace(',', '.');
};

/**
 * Check if input already has 2 decimal places
 * Handles both comma and period as decimal separators
 */
export const hasMaxDecimalPlaces = (currentValue: string, newKey: string): boolean => {
  // Normalize to period for easier checking
  const normalized = normalizeDecimalSeparator(currentValue);
  const decimalIndex = normalized.indexOf('.');

  // If no decimal separator yet, allow typing
  if (decimalIndex === -1) {
    return false;
  }

  // Count digits after decimal separator
  const digitsAfterDecimal = normalized.substring(decimalIndex + 1).length;

  // If already 2 digits after decimal and user is typing a digit, prevent it
  if (digitsAfterDecimal >= 2 && /[0-9]/.test(newKey)) {
    return true;
  }

  return false;
};

/**
 * Check if input already has a decimal separator
 */
export const hasDecimalSeparator = (value: string): boolean => {
  return /[.,]/.test(value);
};

/**
 * Convert decimal to percentage string (0.0249 -> "2.49")
 */
export const toPercentage = (value: string): string => {
  const numValue = parseFloat(value);
  return (numValue * 100).toFixed(2);
};

/**
 * Format decimal as percentage for display (0.0249 -> "2.49%")
 */
export const formatPercentage = (value: string): string => {
  const numValue = parseFloat(value);
  return `${(numValue * 100).toFixed(2)}`;
};

/**
 * Get currency symbol based on region
 */
export const getCurrency = (region: keyof DefaultRootPricing): string => {
  switch (region) {
    case PricingSupportingCountries.EUROPE:
      return 'EUR';
    case PricingSupportingCountries.NORTH_AMERICA:
      return 'USD';
    case PricingSupportingCountries.OTHERS:
    default:
      return 'USD';
  }
};

/**
 * Get initial input value from minimal or default pricing
 */
export const getInitialValue = (
  region: keyof DefaultRootPricing,
  field: keyof PricingRate,
  defaultRootPricing: DefaultRootPricing,
  minimalPricing?: MinimalPricing,
): string => {
  const value = minimalPricing ? minimalPricing[region][field] : defaultRootPricing[region][field];
  // Convert to percentage if not fixed amount
  return field === PricingRateFields.FIXED ? value : toPercentage(value);
};

/**
 * Transform form data (percentages) back to decimals for backend
 */
export const transformPricingForBackend = (formData: Record<string, any>): Record<string, any> => {
  return Object.keys(formData).reduce(
    (acc, region) => {
      const regionData = formData[region];
      acc[region] = {
        variable: (parseFloat(normalizeDecimalSeparator(regionData.variable)) / 100).toFixed(4),
        fixed: normalizeDecimalSeparator(regionData.fixed),
        amex: (parseFloat(normalizeDecimalSeparator(regionData.amex)) / 100).toFixed(4),
        instantPayoutFee: (
          parseFloat(normalizeDecimalSeparator(regionData.instantPayoutFee)) / 100
        ).toFixed(4),
      };
      return acc;
    },
    {} as Record<string, any>,
  );
};

/**
 * Validate pricing value is within allowed range
 * @param enteredValue - The value entered by user
 * @param defaultValue - The default/maximum allowed value
 * @param minimalAllowedValue - Optional minimum allowed value
 * @param isFixedAmount - Whether this is a fixed amount (no percentage conversion needed)
 * @returns true if valid, error key string if invalid
 */
export const validatePricingValue = (
  enteredValue: string,
  defaultValue: string | undefined,
  minimalAllowedValue: string | undefined,
  isFixedAmount: boolean,
):
  | 'invalid_field'
  | 'pricing_lower_than_minimal'
  | 'minimal_pricing_bigger_than_default'
  | 'pricing_bigger_than_maximum'
  | true => {
  // Normalize comma to period before parsing
  const normalizedValue = normalizeDecimalSeparator(enteredValue);
  const entered = parseFloat(normalizedValue);
  if (Number.isNaN(entered)) return 'invalid_field';

  // Convert default value to same unit as entered value

  if (defaultValue && defaultValue !== '') {
    const defaultLimit = isFixedAmount
      ? parseFloat(defaultValue)
      : parseFloat(toPercentage(defaultValue));
    if (entered > defaultLimit) return 'minimal_pricing_bigger_than_default';
    if (entered > defaultLimit) return 'minimal_pricing_bigger_than_default';
  }

  // If minimalAllowedValue exists, validate it's between min and max
  if (minimalAllowedValue) {
    const minimalLimit = isFixedAmount
      ? parseFloat(minimalAllowedValue)
      : parseFloat(toPercentage(minimalAllowedValue));

    if (entered < minimalLimit) return 'pricing_lower_than_minimal';
    if (100 < entered) {
      return 'pricing_bigger_than_maximum';
    }
  } else {
    // If no minimalAllowedValue, just validate it's under default
  }

  return true;
};

export const organizationColumns = {
  numberOfColumns: 4,
  columnsSize: 'minmax(0, 240px) minmax(0, 150px) minmax(0, 244px) minmax(0, 107px)',
};

export const finalColumns = {
  numberOfColumns: 3,
  columnsSize: 'minmax(0, 240px) minmax(0, 2fr) minmax(0, 184px)',
};

export const getTableData = (form: PricingForms) => {
  switch (form) {
    case PricingForms.ORGANIZATION:
      return organizationColumns;
    case PricingForms.FINAL:
      return finalColumns;
  }
};

/**
 * Get all field calculation values for pricing form
 */
type GetFieldCalculationsParams = {
  region: keyof DefaultRootPricing;
  fieldName: keyof PricingRate;
  minimalAllowed: MinimalPricing;
  inputPricing: MinimalPricing;
};

export const getFieldCalculations = ({
  region,
  fieldName,
  minimalAllowed,
  inputPricing,
}: GetFieldCalculationsParams) => {
  const isFixedAmount = fieldName === PricingRateFields.FIXED;
  const minimalAllowedValue = minimalAllowed?.[region]?.[fieldName];

  const displayMinimalAllowedValue = minimalAllowedValue
    ? isFixedAmount
      ? minimalAllowedValue
      : formatPercentage(minimalAllowedValue)
    : undefined;

  const inputValue = getInitialValue(region, fieldName, minimalAllowed, inputPricing);

  const suffix = isFixedAmount ? getCurrency(region) : '%';
  const fieldPath = `${region}.${fieldName}`;

  return {
    isFixedAmount,
    minimalAllowedValue,
    displayMinimalAllowedValue,
    inputValue,
    suffix,
    fieldPath,
  };
};

/**
 * Get instant payout fee calculation values
 */
type GetInstantPayoutCalculationsParams = {
  region: keyof DefaultRootPricing;
  minimalAllowed: MinimalPricing;
  inputPricing: MinimalPricing;
};

export const getInstantPayoutCalculations = ({
  region,
  minimalAllowed,
  inputPricing,
}: GetInstantPayoutCalculationsParams) => {
  const defaultValue = minimalAllowed[region].instantPayoutFee;
  const minimalAllowedValue = minimalAllowed?.[region]?.[PricingRateFields.INSTANT_PAYOUT_FEE];

  const displayMinimalAllowedValue = minimalAllowedValue
    ? formatPercentage(minimalAllowedValue)
    : undefined;

  const displayDefaultValue = formatPercentage(defaultValue);

  const inputValue = getInitialValue(
    region,
    PricingRateFields.INSTANT_PAYOUT_FEE,
    minimalAllowed,
    inputPricing,
  );

  const fieldPath = `${region}.instantPayoutFee`;
  const suffix = '%'; // instantPayoutFee is always percentage

  return {
    defaultValue,
    minimalAllowedValue,
    displayMinimalAllowedValue,
    displayDefaultValue,
    inputValue,
    fieldPath,
    suffix,
  };
};

/**
 * Format the amount row display string
 */
type FormatAmountDisplayParams = {
  region: keyof DefaultRootPricing;
  regionInputData: {
    variable?: string;
    fixed?: string;
    amex?: string;
  };
  t: (key: string) => string;
};

export const formatAmountDisplay = ({
  region,
  regionInputData,
  t,
}: FormatAmountDisplayParams): string => {
  const currency = getCurrency(region);
  const variable = regionInputData?.variable || '0';
  const fixed = regionInputData?.fixed || '0';
  const amex = regionInputData?.amex || '0';

  return `${parseFloat(variable).toFixed(2)} %  +  ${parseFloat(fixed).toFixed(2)} ${currency}  (+ ${parseFloat(amex).toFixed(2)} % ${t('Pricing.for_amex_cards')})`;
};

export type PricingTableDataArgs = {
  region: keyof DefaultRootPricing;
  fieldName: keyof PricingRate;
  minimalAllowed: MinimalPricing;
  inputPricing: MinimalPricing;
  defaultRootPricing: DefaultRootPricing;
  watch: UseFormWatch<any>;
};

/**
 * Build default values object for form from defaultRootPricing and inputPricing
 */
export const buildDefaultValues = (
  defaultRootPricing: DefaultRootPricing,
  inputPricing?: MinimalPricing,
): Record<string, any> => {
  const defaultValues: Record<string, any> = {};
  const regions = Object.keys(defaultRootPricing) as Array<keyof DefaultRootPricing>;

  regions.forEach((region) => {
    const regionData = defaultRootPricing[region];
    const fields = Object.keys(regionData) as Array<keyof PricingRate>;

    defaultValues[region] = {};

    fields.forEach((field) => {
      const value = getInitialValue(region, field, defaultRootPricing, inputPricing);
      defaultValues[region][field] = value;
    });
  });

  return defaultValues;
};

export const getFixedFeeTooltip = (
  region: keyof DefaultRootPricing,
  fixedFees: Record<string, Record<string, any>>,
): string => {
  const regionFees = fixedFees[region];
  const fees = Object.entries(regionFees).map(([key, value]) => {
    const minorUnits = getMinorUnits(key as CurrencyCode);
    const stringRate = formatMinorUnitsToString(value.rate, minorUnits);
    return `• ${key} ${stringRate} (${value.country})`;
  });

  const feesString = fees.join('\n');
  return feesString;
};

export enum ResidualStatus {
  PAID = 'paid',
  DUE = 'due',
  ACCRUING = 'accruing',
}
